import { createFileRoute } from "@tanstack/react-router";
import { Logo } from "@/components/logo";
import { LogoMark } from "@/components/brand/logo-svg";

export const Route = createFileRoute("/dev/logo-preview")({
  component: LogoPreview,
  head: () => ({ meta: [{ title: "Logo preview — lexaipro" }, { name: "robots", content: "noindex" }] }),
});

function Swatch({
  label,
  bg,
  fg = "auto",
}: {
  label: string;
  bg: string;
  fg?: "auto" | "light" | "dark";
}) {
  return (
    <div className="rounded-2xl border border-border overflow-hidden">
      <div
        className="flex items-center justify-center p-10 min-h-[140px]"
        style={{ background: bg }}
      >
        <Logo asLink={false} variant={fg} size={48} />
      </div>
      <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground bg-card">
        <span>{label}</span>
        <code className="font-mono">variant="{fg}"</code>
      </div>
    </div>
  );
}

function LogoPreview() {
  return (
    <main className="min-h-screen bg-background text-foreground py-12 px-6">
      <div className="mx-auto max-w-6xl space-y-10">
        <header className="space-y-2">
          <h1 className="font-display text-4xl tracking-tight">Logo preview</h1>
          <p className="text-muted-foreground">
            SVG-based, scalable, color via <code className="font-mono">currentColor</code>.
            Auto adapts to text color; light/dark variants force contrast.
          </p>
        </header>

        {/* Sizes */}
        <section className="space-y-4">
          <h2 className="font-display text-2xl">Sizes</h2>
          <div className="flex flex-wrap items-end gap-8 rounded-2xl border border-border p-8 bg-card">
            {[20, 28, 36, 48, 64, 96].map((s) => (
              <div key={s} className="flex flex-col items-center gap-2">
                <Logo asLink={false} variant="dark" size={s} />
                <span className="text-xs text-muted-foreground font-mono">{s}px</span>
              </div>
            ))}
          </div>
        </section>

        {/* Mark only */}
        <section className="space-y-4">
          <h2 className="font-display text-2xl">Icon mark only</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="rounded-2xl border border-border p-8 bg-white flex justify-center">
              <LogoMark className="h-20 w-20 text-[#0a0a0a]" />
            </div>
            <div className="rounded-2xl border border-border p-8 bg-black flex justify-center">
              <LogoMark className="h-20 w-20 text-white" />
            </div>
            <div className="rounded-2xl border border-border p-8 bg-[#0066ff] flex justify-center">
              <LogoMark className="h-20 w-20 text-white" />
            </div>
            <div className="rounded-2xl border border-border p-8 bg-[#fafafa] flex justify-center">
              <LogoMark className="h-20 w-20 text-[#0a0a0a]" />
            </div>
          </div>
        </section>

        {/* Backgrounds */}
        <section className="space-y-4">
          <h2 className="font-display text-2xl">On different backgrounds</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Swatch label="White" bg="#ffffff" fg="dark" />
            <Swatch label="Off-white" bg="#fafafa" fg="dark" />
            <Swatch label="Light gray" bg="#e5e7eb" fg="dark" />
            <Swatch label="Pure black" bg="#000000" fg="light" />
            <Swatch label="Charcoal" bg="#0f1115" fg="light" />
            <Swatch label="Brand blue" bg="#0066ff" fg="light" />
            <Swatch label="Emerald" bg="#047857" fg="light" />
            <Swatch label="Crimson" bg="#b91c1c" fg="light" />
            <Swatch label="Auto (inherits)" bg="#fafafa" fg="auto" />
          </div>
        </section>

        {/* Components */}
        <section className="space-y-4">
          <h2 className="font-display text-2xl">In components</h2>

          {/* Dark navbar */}
          <div className="rounded-2xl overflow-hidden border border-border">
            <div className="nav-dark flex items-center justify-between px-6 py-3">
              <Logo asLink={false} variant="light" size={40} />
              <nav className="flex items-center gap-6 text-sm">
                <span>Features</span>
                <span>Pricing</span>
                <span>Login</span>
              </nav>
            </div>
            <div className="px-4 py-2 text-xs text-muted-foreground bg-card">Dark navbar — variant="light", 40px</div>
          </div>

          {/* Light navbar */}
          <div className="rounded-2xl overflow-hidden border border-border">
            <div className="bg-white flex items-center justify-between px-6 py-3 border-b border-border">
              <Logo asLink={false} variant="dark" size={36} />
              <nav className="flex items-center gap-6 text-sm text-foreground">
                <span>Features</span>
                <span>Pricing</span>
                <span>Login</span>
              </nav>
            </div>
            <div className="px-4 py-2 text-xs text-muted-foreground bg-card">Light navbar — variant="dark", 36px</div>
          </div>

          {/* Card */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border p-6 bg-card">
              <Logo asLink={false} variant="dark" size={32} />
              <h3 className="mt-4 font-display text-xl">Light card</h3>
              <p className="text-sm text-muted-foreground">Logo inherits dark color for contrast.</p>
            </div>
            <div className="rounded-2xl p-6 bg-[#0f1115] text-white">
              <Logo asLink={false} variant="light" size={32} />
              <h3 className="mt-4 font-display text-xl">Dark card</h3>
              <p className="text-sm text-white/70">Logo inverts to white on dark surfaces.</p>
            </div>
          </div>
        </section>

        {/* Favicon preview */}
        <section className="space-y-4">
          <h2 className="font-display text-2xl">Favicon &amp; PWA icons</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { src: "/favicon.svg", label: "favicon.svg" },
              { src: "/favicon.ico", label: "favicon.ico" },
              { src: "/apple-touch-icon.png", label: "apple-touch-icon" },
              { src: "/icon-192.png", label: "icon-192" },
              { src: "/icon-512.png", label: "icon-512" },
            ].map((i) => (
              <div key={i.src} className="rounded-2xl border border-border p-6 bg-card flex flex-col items-center gap-3">
                <img src={i.src} alt={i.label} className="h-20 w-20 object-contain" />
                <code className="text-xs font-mono text-muted-foreground">{i.label}</code>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
